import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from 'react';

export interface ConsoleBreadcrumbItem {
    label: string;
    /** Omit on the current (last) item — it renders as plain text, never a link. */
    to?: string;
}

type SetBreadcrumbItems = Dispatch<SetStateAction<ConsoleBreadcrumbItem[] | null>>;

// WHY two contexts instead of one `{ items, setItems }` value: the single
// context was an infinite render loop, and every page that set a breadcrumb hit
// it (~980 "Maximum update depth exceeded" warnings on one visit to
// `/console/apps/:code`). The provider memoised its value on `[items]`, so the
// moment `useConsoleBreadcrumb`'s effect called `setItems`, the context value
// became a NEW object; `ctx` was in that effect's dependency array, so the
// effect tore down (`setItems(null)`) and re-ran (`setItems(items)`), which
// changed `items` again — forever. Splitting the state from the setter fixes it
// at the root: `setItems` is a `useState` setter and is referentially stable for
// the provider's whole life, so the write side has no changing dependency to
// react to. It also stops every writer re-rendering whenever the items change,
// which it never needed to do.
const ConsoleBreadcrumbItemsContext = createContext<ConsoleBreadcrumbItem[] | null>(null);
const ConsoleBreadcrumbSetterContext = createContext<SetBreadcrumbItems | null>(null);

/**
 * EAA-1846 (design frame — the topbar carries "Ứng dụng & quyền / Danh mục
 * ứng dụng / Thêm ứng dụng", not the page body): a per-route breadcrumb slot
 * for `ConsoleTopbar`, the shared shell chrome every console page renders
 * under. The topbar itself is page-agnostic — this is the seam that lets one
 * page feed it something to show without every OTHER page growing an unused
 * prop.
 *
 * Read the raw design HTML directly (not a screenshot or a prior summary)
 * before assuming this is one page's fix: the same breadcrumb-in-topbar
 * position appears on the Apps registry frame too. `AppsCreatePage` is the
 * first adopter; other pages (e.g. `AppDetailPage`, which still renders its
 * own in-body breadcrumb) can move onto this same seam later without
 * touching the provider itself.
 */
export function ConsoleBreadcrumbProvider({ children }: { children: ReactNode }) {
    const [items, setItems] = useState<ConsoleBreadcrumbItem[] | null>(null);
    return (
        <ConsoleBreadcrumbSetterContext.Provider value={setItems}>
            <ConsoleBreadcrumbItemsContext.Provider value={items}>
                {children}
            </ConsoleBreadcrumbItemsContext.Provider>
        </ConsoleBreadcrumbSetterContext.Provider>
    );
}

/** `ConsoleTopbar`'s own read side — `null` means "nothing to show", not an error. */
export function useConsoleBreadcrumbItems(): ConsoleBreadcrumbItem[] | null {
    return useContext(ConsoleBreadcrumbItemsContext);
}

/**
 * A page calls this with its own breadcrumb trail. Set on mount, cleared on
 * unmount — so navigating away (including to a page that sets its own) never
 * leaves a stale trail behind, and a page that adopts no breadcrumb at all
 * correctly shows none rather than inheriting the previous page's.
 *
 * The dependency key is a JSON serialization of `items` rather than `items`
 * itself: callers pass a fresh array literal every render (the common case,
 * e.g. built inline from `t(...)` calls), and comparing by reference would
 * re-fire the effect every render for no reason. Small, string-only arrays
 * make this cheap and exact rather than an approximation. `items` is then read
 * through a ref so the effect always writes the CURRENT trail without listing
 * an every-render-new array as a dependency.
 */
export function useConsoleBreadcrumb(items: ConsoleBreadcrumbItem[]): void {
    const setItems = useContext(ConsoleBreadcrumbSetterContext);
    const key = JSON.stringify(items);
    const latestItems = useRef(items);
    latestItems.current = items;

    useEffect(() => {
        if (!setItems) return;
        setItems(latestItems.current);
        return () => setItems(null);
    }, [setItems, key]);
}
